import { useModalStore, type ModalData } from '@/stores/useModal'
import SubscriptionModal from '@/ui/components/Modal/SubscriptionModal.vue'
import DisableAutopaymentsModal from '@/ui/components/Modal/DisableAutopaymentsModal.vue'
import PurchaseConfirmationModal from '@/ui/components/Modal/PurchaseConfirmationModal.vue'
import ReferralModal from '@/ui/components/Modal/ReferralModal.vue'
import SupportTicketModal from '@/ui/components/Modal/SupportTicketModal.vue'
import ChangePasswordModal from '@/ui/components/Modal/ChangePasswordModal.vue'
import AuthSuccessModal from '@/ui/components/Modal/AuthSuccessModal.vue'
import SetCredentialsModal from '@/ui/components/Modal/SetCredentialsModal.vue'
import type { PaymentMethod } from '@/entities/Payment/model/types'

export const useModal = () => {
  const modalStore = useModalStore()

  const openModal = (modal: Omit<ModalData, 'id'>) => {
    return modalStore.openModal(modal)
  }

  const closeModal = (id: string) => {
    modalStore.closeModal(id)
  }

  const closeLastModal = () => {
    modalStore.closeLastModal()
  }

  const closeAllModals = () => {
    modalStore.closeAllModals()
  }

  // Удобные методы для конкретных типов модальных окон
  const openSubscriptionModal = (props?: {
    period?: string
    endDate?: string
    price?: number
    months?: number
    autopaymentEnabled?: boolean
    subscriptionId?: string
    onTinkoffPayment?: () => void
    onYookassaPayment?: () => void
    onPayment?: (method: PaymentMethod, email?: string) => void | Promise<void>
    forceEmailInput?: boolean
    emailErrorMessage?: string
    paymentErrorMessage?: string
    onClose?: () => void // Callback при закрытии модалки
  }) => {
    return openModal({
      component: SubscriptionModal,
      props: {
        period: props?.period,
        endDate: props?.endDate,
        price: props?.price,
        months: props?.months,
        autopaymentEnabled: props?.autopaymentEnabled,
        subscriptionId: props?.subscriptionId,
        onTinkoffPayment: props?.onTinkoffPayment,
        onYookassaPayment: props?.onYookassaPayment,
        onPayment: props?.onPayment,
        forceEmailInput: props?.forceEmailInput,
        emailErrorMessage: props?.emailErrorMessage,
        paymentErrorMessage: props?.paymentErrorMessage,
      },
      showCloseButton: true,
      onClose: props?.onClose
    })
  }

  const openConfirmModal = (props: {
    title: string
    content: string
    onConfirm?: () => void
    onCancel?: () => void
    confirmText?: string
    cancelText?: string
  }) => {
    return openModal({
      title: props.title,
      content: props.content,
      showCloseButton: true
    })
  }

  const openAlertModal = (props: {
    title: string
    content: string
    onOk?: () => void
    okText?: string
  }) => {
    return openModal({
      title: props.title,
      content: props.content,
      showCloseButton: true
    })
  }

  const openDisableAutopaymentsModal = (props?: {
    onClose?: () => void
  }) => {
    return openModal({
      component: DisableAutopaymentsModal,
      showCloseButton: true,
      onClose: props?.onClose
    })
  }

  const openPurchaseConfirmationModal = (props?: {
    onNewSubscription?: () => void
    onExtendSubscription?: () => void
    onClose?: () => void
  }) => {
    return openModal({
      component: PurchaseConfirmationModal,
      props: {
        onNewSubscription: props?.onNewSubscription,
        onExtendSubscription: props?.onExtendSubscription
      },
      showCloseButton: true,
      onClose: props?.onClose
    })
  }

  const openReferralModal = (props?: {
    onClose?: () => void
  }) => {
    return openModal({
      component: ReferralModal,
      showCloseButton: true,
      onClose: props?.onClose
    })
  }

  const openSupportTicketModal = (props: {
    onSubmit: (message: string) => void;
    onClose?: () => void;
  }) => {
    return openModal({
      component: SupportTicketModal,
      props: {
        onSubmit: props.onSubmit,
      },
      showCloseButton: true,
      onClose: props.onClose,
    });
  };

  const openChangePasswordModal = (props?: {
    onClose?: () => void
  }) => {
    return openModal({
      component: ChangePasswordModal,
      showCloseButton: true,
      onClose: props?.onClose
    })
  }

  const openAuthSuccessModal = (props: {
    title: string
    description: string
    buttonLink: string
    buttonText?: string
    secondaryLinks?: { label: string; href: string }[]
    onClose?: () => void
  }) => {
    return openModal({
      component: AuthSuccessModal,
      props,
      showCloseButton: true,
      onClose: props?.onClose
    })
  }

  const openSetCredentialsModal = (props?: {
    onClose?: () => void
  }) => {
    return openModal({
      component: SetCredentialsModal,
      showCloseButton: true,
      onClose: props?.onClose
    })
  }

  return {
    openModal,
    closeModal,
    closeLastModal,
    closeAllModals,
    openSubscriptionModal,
    openConfirmModal,
    openAlertModal,
    openDisableAutopaymentsModal,
    openPurchaseConfirmationModal,
    openReferralModal,
    openSupportTicketModal,
    openChangePasswordModal,
    openSetCredentialsModal,
    openAuthSuccessModal
  }
}
